import { useState, useRef, useEffect, useCallback } from "react";
import { fmtDec } from "utils/format";

function getCurrentLevel(levels, drillPath) {
  if (!levels) return null;
  let current = levels["all"] ?? (Array.isArray(levels.rows) ? levels : null);
  for (const label of drillPath) {
    const row = current?.rows?.find(r => r.label === label);
    if (!row?.child || !levels[row.child]) return null;
    current = levels[row.child];
  }
  return current;
}

export default function WhatIfAllocCanvas({ levelsArray, palette, theme }) {
  const canvasRef    = useRef(null);
  const rowRectsRef  = useRef([]);
  const maxValRef    = useRef(100);
  const [drillPath, setDrillPath] = useState([]);
  const [tooltip, setTooltip]     = useState(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const isLight    = theme === "light";
    const textColor  = a => isLight ? `rgba(0,0,0,${a})`       : `rgba(255,255,255,${a})`;
    const gridColor  = () => isLight ? "rgba(0,0,0,0.06)"       : "rgba(255,255,255,0.05)";
    const trackColor = () => isLight ? "rgba(0,0,0,0.07)"       : "rgba(255,255,255,0.05)";
    const axisLbl    = () => isLight ? "rgba(0,0,0,0.35)"       : "rgba(255,255,255,0.3)";
    const pctLbl     = () => isLight ? "rgba(0,0,0,0.85)"       : "rgba(255,255,255,0.92)";

    const currentLevels = (levelsArray || []).map(lv => getCurrentLevel(lv, drillPath));
    const activeLevels  = currentLevels.filter(Boolean);
    if (!activeLevels.length) return;

    // Union of category labels in first-occurrence order
    const labelOrder = [];
    const labelSet   = new Set();
    activeLevels.forEach(lv => {
      lv.rows.forEach(r => {
        if (!labelSet.has(r.label)) { labelSet.add(r.label); labelOrder.push(r.label); }
      });
    });
    if (!labelOrder.length) return;

    const nP     = (levelsArray || []).length;
    const dpr    = window.devicePixelRatio || 1;
    const W      = (canvas.parentElement && canvas.parentElement.clientWidth) || 300;

    const barH = 14, barGap = 2, classGap = 10;
    const labelW = 100, padTop = 6, axisH = 20, padBot = 4;
    const groupH = nP * barH + (nP - 1) * barGap;
    const H      = padTop + labelOrder.length * (groupH + classGap) - classGap + axisH + padBot;

    canvas.width        = W * dpr;
    canvas.height       = H * dpr;
    canvas.style.width  = W + "px";
    canvas.style.height = H + "px";

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const trackW = W - labelW - 8;

    // Global max across all portfolios and all categories
    let maxVal = 0;
    labelOrder.forEach(label => {
      currentLevels.forEach(lv => {
        if (!lv) return;
        const row = lv.rows.find(r => r.label === label);
        if (row) maxVal = Math.max(maxVal, (row.mv || 0) + (row.var || 0));
      });
    });
    if (maxVal === 0) maxVal = 100;
    maxValRef.current = maxVal;

    rowRectsRef.current = [];

    labelOrder.forEach((label, li) => {
      const groupY = padTop + li * (groupH + classGap);

      const isDrillable = currentLevels.some(lv => {
        if (!lv) return false;
        const row = lv.rows.find(r => r.label === label);
        return row?.child != null;
      });

      ctx.font = "400 11px 'Outfit', sans-serif";
      ctx.fillStyle = textColor(0.85);
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(label, labelW - (isDrillable ? 10 : 6), groupY + groupH / 2);

      if (isDrillable) {
        ctx.font = "500 10px 'Outfit', sans-serif";
        ctx.fillStyle = "#4f6ef7";
        ctx.textAlign = "right";
        ctx.fillText("›", labelW - 1, groupY + groupH / 2 + 0.5);
      }

      currentLevels.forEach((lv, pi) => {
        const row    = lv?.rows?.find(r => r.label === label);
        const mv     = row?.mv  ?? 0;
        const varVal = row?.var ?? 0;
        const mvPx   = (mv     / maxVal) * trackW;
        const varPx  = (varVal / maxVal) * trackW;
        const x0     = labelW;
        const y      = groupY + pi * (barH + barGap);
        const cy     = y + barH / 2;
        const color  = (palette || [])[pi % ((palette || []).length || 1)] || "#4e8fdd";

        ctx.fillStyle = trackColor();
        ctx.beginPath(); ctx.roundRect(x0, y, trackW, barH, 2); ctx.fill();

        if (mvPx > 0) {
          ctx.fillStyle = color + "bb";
          ctx.beginPath(); ctx.roundRect(x0, y, mvPx, barH, varPx > 0 ? [2, 0, 0, 2] : 2); ctx.fill();
        }
        if (varPx > 0) {
          ctx.fillStyle = color + "55";
          ctx.beginPath(); ctx.roundRect(x0 + mvPx, y, varPx, barH, [0, 2, 2, 0]); ctx.fill();
        }

        ctx.font = "500 9px 'DM Mono', monospace";
        ctx.textBaseline = "middle";
        if (mvPx >= 22) {
          ctx.fillStyle = pctLbl(); ctx.textAlign = "center";
          ctx.fillText(fmtDec(mv) + "%", x0 + mvPx / 2, cy);
        }
        if (varPx >= 22) {
          ctx.fillStyle = pctLbl(); ctx.textAlign = "center";
          ctx.fillText(fmtDec(varVal) + "%", x0 + mvPx + varPx / 2, cy);
        }
      });

      rowRectsRef.current.push({ y: groupY, h: groupH, label, isDrillable });
    });

    // X-axis
    const axisY = H - axisH + 4;
    ctx.strokeStyle = gridColor(); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(labelW, axisY); ctx.lineTo(labelW + trackW, axisY); ctx.stroke();
    ctx.font = "400 9px 'DM Mono', monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "top";
    for (let t = 0; t <= 5; t++) {
      const px = labelW + (t / 5) * trackW;
      ctx.strokeStyle = axisLbl(); ctx.lineWidth = 0.75;
      ctx.beginPath(); ctx.moveTo(px, axisY); ctx.lineTo(px, axisY + 3); ctx.stroke();
      ctx.fillStyle = axisLbl();
      ctx.fillText(Math.round((t / 5) * maxVal) + "%", px, axisY + 5);
    }
  }, [levelsArray, drillPath, theme, palette]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas?.parentElement) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, [draw]);

  const handleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const my   = e.clientY - rect.top;
    for (const r of rowRectsRef.current) {
      if (my >= r.y && my <= r.y + r.h && r.isDrillable) {
        setDrillPath(prev => [...prev, r.label]);
        return;
      }
    }
  };

  const handleMouseMove = (e) => {
    const canvas  = canvasRef.current;
    const rect    = canvas.getBoundingClientRect();
    const my      = e.clientY - rect.top;
    const mx      = e.clientX - rect.left;
    const labelW  = 100;
    const barH    = 14, barGap = 2;

    let hit = null;
    for (const r of rowRectsRef.current) {
      if (my >= r.y && my <= r.y + r.h && mx > labelW) {
        const relY = my - r.y;
        const pi   = Math.min(Math.floor(relY / (barH + barGap)), (levelsArray || []).length - 1);
        const lv   = getCurrentLevel((levelsArray || [])[pi], drillPath);
        const row  = lv?.rows?.find(rr => rr.label === r.label);
        if (row) {
          const trackW = rect.width - labelW - 8;
          const mvPx   = (row.mv / maxValRef.current) * trackW;
          if (mx <= labelW + mvPx) {
            hit = { series: "Alloc", value: row.mv };
          } else if (row.var > 0) {
            hit = { series: "VaR Contrib", value: row.var };
          }
        }
        break;
      }
    }
    setTooltip(hit ? { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, ...hit } : null);
    canvas.style.cursor = rowRectsRef.current.some(r => my >= r.y && my <= r.y + r.h && r.isDrillable)
      ? "pointer" : "default";
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
      {drillPath.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.6rem", fontFamily: "var(--dash-font-m)", flexWrap: "wrap", paddingBottom: "0.2rem" }}>
          <span
            onClick={() => setDrillPath([])}
            style={{ color: "var(--dash-blue)", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
          >
            All
          </span>
          {drillPath.map((label, i) => {
            const isLast = i === drillPath.length - 1;
            return (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "var(--dash-muted)" }}>›</span>
                {isLast ? (
                  <span style={{ color: "var(--dash-text2)" }}>{label}</span>
                ) : (
                  <span
                    onClick={() => setDrillPath(prev => prev.slice(0, i + 1))}
                    style={{ color: "var(--dash-blue)", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                  >
                    {label}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}

      <div style={{ position: "relative" }}>
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ display: "block", width: "100%" }}
        />
        {tooltip && (
          <div style={{
            position: "absolute",
            left: tooltip.x + 12,
            top: tooltip.y - 10,
            pointerEvents: "none",
            background: "var(--dash-card-bg, #1a2035)",
            border: "1px solid var(--dash-border, rgba(255,255,255,0.1))",
            borderRadius: 5,
            padding: "4px 8px",
            fontSize: "0.65rem",
            fontFamily: "'DM Mono', monospace",
            color: "var(--dash-text2)",
            whiteSpace: "nowrap",
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}>
            <span style={{ color: "var(--dash-muted)" }}>{tooltip.series}: </span>
            {fmtDec(tooltip.value)}%
          </div>
        )}
      </div>
    </div>
  );
}
